import type {
  DraftFieldOverride,
  EvidenceCategory,
  EvidenceItem,
  ObservationStatus,
  QuestionItem,
} from '@/features/text-mode/types/text-mode.types';
import type {
  GuidedModeAnsweredItem,
  GuidedModeDemographicItem,
  GuidedModeDefinition,
} from '@/features/guided-mode/types/guided-mode.types';
import type {
  AssessedCondition,
} from '@/features/overall-assessment/types/overall-assessment.types';
import {
  isStoppedEarly,
  riskAssessmentSection,
  STOPPED_EARLY_NOTE_TOKEN,
} from '@/features/overall-assessment/utils/risk-assessment-section';
import {
  OBSERVATION_STATUS_LABEL_TOKENS,
} from '@/features/text-mode/constants/observation-status.constants';
import {
  formatAgeInDays,
  formatLabel,
  parseNumericWithUnit,
} from '@/features/text-mode/utils/text-utils';
import {
  orderClinicalObservationItems,
  shouldShowObservationValue,
} from '@/features/text-mode/utils/clinical-observation-display';
import { attributeSuffix } from './attributeSuffix';

export interface CopyIdentifier {
  label: string;
  value?: string | number | null;
}

export interface CopySection {
  label: string;
  lines: string[];
}

export interface CopyFormatters {
  translate: (token: string) => string;
  formatNumber?: (value: number) => string;
  rtl?: boolean;
}

export interface EvidenceCopyState {
  draftOverrides?: Record<string, DraftFieldOverride>;
  committedValues?: Record<string, string | number | boolean | null>;
  committedIncorrectIds?: Set<string>;
  committedObservationStatuses?: Record<string, ObservationStatus>;
}

interface BuildGuidedSummaryArgs extends CopyFormatters {
  identifiers: CopyIdentifier[];
  demographics: GuidedModeDemographicItem[];
  symptoms?: { label: string; value: string };
  items: GuidedModeAnsweredItem[];
}

interface BuildEvidenceSummaryArgs extends CopyFormatters {
  identifiers: CopyIdentifier[];
  categories: EvidenceCategory[];
  questions?: QuestionItem[];
  patientId?: string | number | null;
  state?: EvidenceCopyState;
}

interface BuildRiskSectionsArgs {
  assessments: Array<AssessedCondition & { selected?: boolean }>;
  triageOrder: readonly string[];
  translate: (token: string) => string;
  translationExists?: (token: string) => boolean;
  riskAssessmentToken: (name: string, severity: string) => string;
}

interface ResolveEncounterIdentifiersArgs {
  mode: 'text' | 'guided';
  patientId?: string | number | null;
  configuredPanel?: string | null;
  assessmentDefinitions?: Record<string, GuidedModeDefinition>;
  translate: (token: string) => string;
}

const CATEGORY_TITLE_TOKENS: Record<string, string> = {
  "patient": 'app.assessment_mode.text.input.patient_header',
  "vitals": 'app.assessment_mode.text.input.vitals_header',
  "observations": 'app.assessment_mode.text.input.clinical_observations_header',
  'clinical-observations': 'app.assessment_mode.text.input.clinical_observations_header',
  'follow-up-questions': 'app.assessment_mode.text.input.follow_up_questions_header',
};

const CLINICAL_OBSERVATION_COPY_STATUS_TOKENS: Record<ObservationStatus, string> = {
  positive: 'app.assessment_mode.text.input.observation_reported',
  negative: 'app.assessment_mode.text.input.observation_not_reported',
  unknown: 'app.assessment_mode.text.input.option.unknown',
};

function hasValue(value: unknown): value is string | number | boolean {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function withDirection(payload: string, rtl = false): string {
  return rtl && payload ? `\u200F${payload}` : payload;
}

export function buildIdentifierBlock(
  identifiers: CopyIdentifier[],
  translate: CopyFormatters['translate'],
): string {
  const lines = identifiers
    .filter(identifier => hasValue(identifier.value))
    .map(identifier => `${identifier.label}: ${String(identifier.value).trim()}`);

  return lines.length > 0 ? lines.join('\n') : translate('app.encounter.no_id.title');
}

export function formatCopySections(sections: CopySection[]): string[] {
  return sections
    .filter(section => section.lines.length > 0)
    .map(section => {
      const lines = section.lines.map(line => ` • ${line}`).join('\n');
      return `${section.label.toUpperCase()}\n${lines}`;
    });
}

export function formatCopyGroupTitle(title: string): string {
  return `=== ${title} ===`;
}

export function composeCopyPayload(
  identifiers: CopyIdentifier[],
  sections: CopySection[],
  translate: CopyFormatters['translate'],
  rtl = false,
  title = '',
): string {
  const payload = [
    buildIdentifierBlock(identifiers, translate),
    title ? formatCopyGroupTitle(title) : '',
    ...formatCopySections(sections),
  ]
    .filter(Boolean)
    .join('\n\n')
    .trim();

  return withDirection(payload, rtl);
}

export function buildGuidedSummaryCopyPayload({
  identifiers,
  demographics,
  symptoms,
  items,
  translate,
  rtl = false,
}: BuildGuidedSummaryArgs): string {
  const sections: CopySection[] = [
    {
      label: translate('app.summary.patient_demographics.title'),
      lines: demographics.map(item => `${item.label}: ${item.value}`),
    },
    {
      label: translate('app.summary.observations.title'),
      lines: [
        ...(symptoms?.value ? [`${symptoms.label}: ${symptoms.value}`] : []),
        ...items.map(item => `${item.title}: ${item.answer}`),
      ],
    },
  ];

  return composeCopyPayload(
    identifiers, sections, translate, rtl, translate('app.copy.section.assessment_summary'),
  );
}

function effectiveItemValue(
  item: EvidenceItem,
  state: EvidenceCopyState,
): string | number | boolean | null {
  const draft = state.draftOverrides?.[item.id];
  if (draft) return draft.draftValue;
  if (state.committedValues
    && Object.prototype.hasOwnProperty.call(state.committedValues, item.id)) {
    return state.committedValues[item.id];
  }
  return item.value;
}

function effectiveObservationStatus(
  item: EvidenceItem,
  state: EvidenceCopyState,
): ObservationStatus | undefined {
  return state.draftOverrides?.[item.id]?.observationStatus
    ?? state.committedObservationStatuses?.[item.id]
    ?? item.observationStatus;
}

function isRemoved(item: EvidenceItem, state: EvidenceCopyState): boolean {
  return Boolean(
    state.draftOverrides?.[item.id]?.markedIncorrect
    || state.committedIncorrectIds?.has(item.id),
  );
}

function itemLabel(
  item: EvidenceItem,
  status: ObservationStatus | undefined,
  translate: (token: string) => string,
): string {
  const name = item.nameToken ? translate(item.nameToken) : formatLabel(item.name);
  return status === 'positive' ? name + attributeSuffix(item.attributes, translate) : name;
}

function optionValueLabel(
  item: EvidenceItem,
  value: string,
  translate: (token: string) => string,
): string {
  const option = item.responseOptions?.find(candidate => candidate.value === value);
  return option?.token ? translate(option.token) : formatLabel(value);
}

function itemValueLabel(
  item: EvidenceItem,
  value: string | number | boolean | null,
  status: ObservationStatus | undefined,
  isClinicalObservation: boolean,
  translate: (token: string) => string,
  formatNumber: (value: number) => string,
): string {
  if (status) {
    const tokens = isClinicalObservation
      ? CLINICAL_OBSERVATION_COPY_STATUS_TOKENS
      : OBSERVATION_STATUS_LABEL_TOKENS;
    const statusLabel = translate(tokens[status]);
    const showValue = isClinicalObservation
      && hasValue(value)
      && shouldShowObservationValue({ value, responseOptions: item.responseOptions });
    return showValue
      ? `${statusLabel} ${plainValueLabel(item, value, translate, formatNumber)}`
      : statusLabel;
  }
  if (!hasValue(value)) return '';

  return plainValueLabel(item, value, translate, formatNumber);
}

function plainValueLabel(
  item: EvidenceItem,
  value: string | number | boolean,
  translate: (token: string) => string,
  formatNumber: (value: number) => string,
): string {
  if (item.valueFormatter === 'age') {
    return formatAgeInDays(value, translate, formatNumber);
  }

  if (item.valueToken && value === item.value) {
    return translate(item.valueToken);
  }

  if (item.observationType === 'option' && typeof value === 'string') {
    return optionValueLabel(item, value, translate);
  }

  const numericParts = parseNumericWithUnit(value);
  if (numericParts) return `${numericParts.numberValue} ${numericParts.unit}`;

  const unit = item.numericOptions?.unit;
  return unit ? `${String(value)} ${translate(`units.${unit}.short`)}` : String(value);
}

function questionAnswerLabel(
  question: QuestionItem,
  translate: (token: string) => string,
): string {
  if (!hasValue(question.answer)) return '';
  if (question.type === 'option') {
    const response = question.responses.find(candidate => candidate.value === question.answer);
    if (response?.token) return translate(response.token);
  }
  if (question.type === 'number' && question.options.unit) {
    return `${question.answer} ${translate(`units.${question.options.unit}.short`)}`;
  }
  return String(question.answer);
}

function withPatientId(
  categories: EvidenceCategory[],
  patientId: string | number | null | undefined,
): EvidenceCategory[] {
  if (!hasValue(patientId)) return categories;

  return categories.map(category => {
    if (category.id !== 'patient' || category.items.some(item => item.id === 'patient-id')) {
      return category;
    }
    return {
      ...category,
      items: [{
        id: 'patient-id',
        name: 'Patient ID',
        nameToken: 'app.assessment_mode.text.input.patient_id_label',
        value: patientId,
        editable: false,
        readOnly: true,
      }, ...category.items],
    };
  });
}

export function buildEvidenceSummaryCopyPayload({
  identifiers,
  categories,
  questions = [],
  patientId,
  state = {},
  translate,
  formatNumber = String,
  rtl = false,
}: BuildEvidenceSummaryArgs): string {
  const sections = withPatientId(categories, patientId).map((category): CopySection => {
    const categoryId = category.id.toLowerCase();
    const isClinicalObservation = categoryId === 'clinical-observations'
      || categoryId === 'observations';
    const visibleItems = category.items.filter(item => !isRemoved(item, state));
    const orderedItems = isClinicalObservation
      ? orderClinicalObservationItems(
        visibleItems,
        item => effectiveObservationStatus(item, state) ?? null,
      )
      : visibleItems;

    return {
      label: translate(CATEGORY_TITLE_TOKENS[categoryId] || category.title),
      lines: orderedItems
        .map((item) => {
          const value = effectiveItemValue(item, state);
          const status = effectiveObservationStatus(item, state);
          const valueLabel = itemValueLabel(
            item,
            value,
            status,
            isClinicalObservation,
            translate,
            formatNumber,
          );
          return valueLabel ? `${itemLabel(item, status, translate)}: ${valueLabel}` : '';
        })
        .filter(Boolean),
    };
  });

  const questionLines = questions.filter(question => !question.attributeOf).map((question) => {
    const answer = questionAnswerLabel(question, translate);
    if (!answer) return '';
    const label = question.promptToken
      ? translate(question.promptToken)
      : question.prompt || formatLabel(question.id);
    return `${label}: ${answer}`;
  }).filter(Boolean);

  if (questionLines.length > 0) {
    sections.push({
      label: translate(CATEGORY_TITLE_TOKENS['follow-up-questions']),
      lines: questionLines,
    });
  }

  return composeCopyPayload(
    identifiers, sections, translate, rtl, translate('app.copy.section.assessment_summary'),
  );
}

export function buildRiskAssessmentCopySections({
  assessments,
  triageOrder,
  translate,
  translationExists = () => false,
  riskAssessmentToken,
}: BuildRiskSectionsArgs): CopySection[] {
  return triageOrder.map((triage): CopySection => {
    const lines = assessments
      .filter(assessment => (
        assessment.selected === true
        && assessment.severity !== 'not_assessed'
        && riskAssessmentSection(assessment) === triage
      ))
      .flatMap((assessment) => {
        const title = translate(riskAssessmentToken(assessment.name, assessment.severity));
        const severity = assessment.severity === 'risk_none' ? 'none' : assessment.severity;
        const detailToken = `assessment.${assessment.name}.${severity}.triage`;

        const annotations: string[] = [];
        if (translationExists(detailToken)) annotations.push(translate(detailToken));
        // The note the screen shows on the right of the row belongs with the copied row too,
        // otherwise the copied text drops the reason the condition is inconclusive.
        if (isStoppedEarly(assessment)) annotations.push(translate(STOPPED_EARLY_NOTE_TOKEN));

        return [annotations.length > 0
          ? `${title}\n${annotations.map(annotation => `   • ${annotation}`).join('\n')}`
          : title];
      });

    return {
      label: translate(`triage.${triage}.title`),
      lines,
    };
  }).filter(section => section.lines.length > 0);
}

function configuredIdentifierTitleToken(
  definitions: Record<string, GuidedModeDefinition>,
  panel: string,
): string {
  for (const definition of Object.values(definitions)) {
    const conceptId = Object.entries(definition.concepts || {})
      .find(([, concept]) => concept.code?.code === panel)?.[0];
    if (!conceptId) continue;
    const question = Object.values(definition.questions || {})
      .find(candidate => candidate.conceptId === conceptId);
    if (question?.titleToken) return question.titleToken;
  }
  return `questions.${panel}.title`;
}

export function resolveEncounterCopyIdentifiers({
  mode,
  patientId,
  configuredPanel,
  assessmentDefinitions = {},
  translate,
}: ResolveEncounterIdentifiersArgs): CopyIdentifier[] {
  if (mode === 'guided' && configuredPanel) {
    return [{
      label: translate(configuredIdentifierTitleToken(assessmentDefinitions, configuredPanel)),
      value: patientId,
    }];
  }

  return [{
    label: translate('app.assessment_mode.text.input.patient_id_label'),
    value: patientId,
  }];
}
